import { defineStore } from 'pinia';
import { api } from '../api.js';

/**
 * The scope that means every collection, as opposed to a collection that
 * happens to be called something like that. Selecting it is how you search or
 * browse across everything you can reach.
 */
export const ALL = '*';

/**
 * The session and the current collection. Whatever is selected in the collection
 * bar scopes every pane, so it lives here rather than in any one view.
 */
export const useVault = defineStore('vault', {
  state: () => ({
    user: null,
    collections: [],
    currentId: localStorage.getItem('vault:collection') ?? null,
    mcpUrl: '',
    isAdmin: false,
    admins: [],
    loaded: false,
    error: null,
    toast: null,
  }),

  getters: {
    isAll: state => state.currentId === ALL,

    /** Null when the scope is every collection, because that is not one. */
    current(state) {
      if (state.currentId === ALL) return null;
      return state.collections.find(c => c.id === state.currentId) ?? state.collections[0] ?? null;
    },

    /** What a pane should scope a request to: a slug, or nothing for all. */
    scopeId(state) {
      return state.currentId === ALL ? undefined : this.current?.id;
    },

    /** Somewhere to write when the scope itself is not writable. */
    firstWritable(state) {
      return state.collections.find(c => ['owner', 'editor'].includes(c.role)) ?? null;
    },
    canWrite() {
      return this.current ? ['owner', 'editor'].includes(this.current.role) : false;
    },
    isOwner() {
      return this.current?.role === 'owner';
    },
    signedIn: state => !!state.user,
  },

  actions: {
    /**
     * Never throws. The router guard awaits this before every navigation, and a
     * rejection there aborts the navigation and leaves a blank page, which is a
     * worse thing to show someone than a sign-in form or an error.
     */
    async load() {
      try {
        const me = await api.me();
        this.user = me.user;
        this.collections = me.collections;
        this.mcpUrl = me.mcpUrl;
        this.isAdmin = !!me.isAdmin;
        this.admins = me.admins ?? [];
        this.error = null;
        if (this.currentId !== ALL && !this.collections.some(c => c.id === this.currentId)) {
          this.currentId = this.collections[0]?.id ?? null;
        }
      } catch (err) {
        this.user = null;
        this.collections = [];
        this.isAdmin = false;
        // 401 is the ordinary signed-out case. Anything else is worth saying.
        this.error = err.status === 401 ? null : (err.message || 'The vault is not answering');
      } finally {
        this.loaded = true;
      }
    },

    async refreshCollections() {
      this.collections = await api.collections();
    },

    select(id) {
      this.currentId = id;
      localStorage.setItem('vault:collection', id);
    },

    async signIn(email, password) {
      const result = await api.signIn(email, password);
      // A sign-in that resumes an OAuth authorization comes back as a redirect.
      if (result?.redirect && result.url) {
        window.location.assign(result.url);
        return { redirected: true };
      }
      await this.load();
      return { redirected: false };
    },

    async signOut() {
      await api.signOut();
      this.user = null;
      this.collections = [];
    },

    say(message, { undo = null, tone = 'ok' } = {}) {
      this.toast = { message, undo, tone, at: Date.now() };
    },

    hush() {
      this.toast = null;
    },
  },
});

export function people(n) {
  return `${n} ${n === 1 ? 'person' : 'people'}`;
}

/**
 * Timestamps arrive two ways: our own tables store epoch milliseconds, Better
 * Auth's store ISO strings. Take either.
 */
function epoch(value) {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isFinite(n)) return n;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function ago(value) {
  const ms = epoch(value);
  if (!ms) return 'never';
  const s = Math.max(1, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function when(value) {
  const ms = epoch(value);
  return ms ? new Date(ms).toLocaleString() : 'unknown';
}

export function bytes(n) {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
